import React, { useReducer, useRef, useCallback, useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import useMouse from 'react-use/lib/useMouse';

import {
  ADD_APP,
  ADD_ICON,
  DEL_APP,
  FOCUS_APP,
  MINIMIZE_APP,
  TOGGLE_MAXIMIZE_APP,
  FOCUS_ICON,
  SELECT_ICONS,
  FOCUS_DESKTOP,
  START_SELECT,
  END_SELECT,
  POWER_OFF,
  CANCEL_POWER_OFF,
} from './constants/actions';
import { FOCUSING, POWER_STATE } from './constants';
import { defaultIconState, defaultAppState, appSettings, DoNotOpen, basementIconEntry, bootApps, casinoDirect } from './apps';
import { PRACTICE as CASINO_PRACTICE } from 'lib/casino';
import { SOCIAL } from './apps';
import { findGame, openGame } from './apps/Games/catalog';
import Boot from './Boot';
import Screensaver from './Screensaver';
import Modal from './Modal';
import Footer from './Footer';
import Windows from './Windows';
import Icons from './Icons';
import { DashedBox } from 'components';
import wallpaper from 'assets/wallpaper.jpg';

// My Pictures can set any picture as the desktop background; it survives a reload like XP's did.
const WALLPAPER_KEY = 'ic:wallpaper';

const bootList = bootApps();
const initState = {
  apps: bootList,
  nextAppID: bootList.length,
  nextZIndex: bootList.length,
  focusing: FOCUSING.WINDOW,
  icons: defaultIconState,
  selecting: false,
  powerState: POWER_STATE.START,
};
const reducer = (state, action = { type: '' }) => {
  switch (action.type) {
    case ADD_APP:
      // instanceKey: a multi-instance component (Arcade, IE) can still dedupe per thing —
      // one DOOM window, one Time Machine — while plain instances keep stacking.
      const app = state.apps.find(
        _app =>
          _app.component === action.payload.component &&
          (!action.payload.instanceKey || _app.instanceKey === action.payload.instanceKey),
      );
      if ((action.payload.multiInstance && !action.payload.instanceKey) || !app) {
        return {
          ...state,
          apps: [
            ...state.apps,
            {
              ...action.payload,
              id: state.nextAppID,
              zIndex: state.nextZIndex,
            },
          ],
          nextAppID: state.nextAppID + 1,
          nextZIndex: state.nextZIndex + 1,
          focusing: FOCUSING.WINDOW,
        };
      }
      const apps = state.apps.map(_app =>
        _app.id === app.id
          ? { ..._app, zIndex: state.nextZIndex, minimized: false }
          : _app,
      );
      return {
        ...state,
        apps,
        nextZIndex: state.nextZIndex + 1,
        focusing: FOCUSING.WINDOW,
      };
    case ADD_ICON:
      if (state.icons.some(i => i.id === action.payload.id)) return state;
      return { ...state, icons: [...state.icons, action.payload] };
    case DEL_APP:
      if (state.focusing !== FOCUSING.WINDOW) return state;
      return {
        ...state,
        apps: state.apps.filter(app => app.id !== action.payload),
        focusing:
          state.apps.length > 1
            ? FOCUSING.WINDOW
            : state.icons.find(icon => icon.isFocus)
            ? FOCUSING.ICON
            : FOCUSING.DESKTOP,
      };
    case FOCUS_APP: {
      const apps = state.apps.map(app =>
        app.id === action.payload
          ? { ...app, zIndex: state.nextZIndex, minimized: false }
          : app,
      );
      return {
        ...state,
        apps,
        nextZIndex: state.nextZIndex + 1,
        focusing: FOCUSING.WINDOW,
      };
    }
    case MINIMIZE_APP: {
      if (state.focusing !== FOCUSING.WINDOW) return state;
      const apps = state.apps.map(app =>
        app.id === action.payload ? { ...app, minimized: true } : app,
      );
      return {
        ...state,
        apps,
        focusing: FOCUSING.WINDOW,
      };
    }
    case TOGGLE_MAXIMIZE_APP: {
      if (state.focusing !== FOCUSING.WINDOW) return state;
      const apps = state.apps.map(app =>
        app.id === action.payload ? { ...app, maximized: !app.maximized } : app,
      );
      return {
        ...state,
        apps,
        focusing: FOCUSING.WINDOW,
      };
    }
    case FOCUS_ICON: {
      const icons = state.icons.map(icon => ({
        ...icon,
        isFocus: icon.id === action.payload,
      }));
      return {
        ...state,
        focusing: FOCUSING.ICON,
        icons,
      };
    }
    case SELECT_ICONS: {
      const icons = state.icons.map(icon => ({
        ...icon,
        isFocus: action.payload.includes(icon.id),
      }));
      return {
        ...state,
        icons,
        focusing: FOCUSING.ICON,
      };
    }
    case FOCUS_DESKTOP:
      return {
        ...state,
        focusing: FOCUSING.DESKTOP,
        icons: state.icons.map(icon => ({
          ...icon,
          isFocus: false,
        })),
      };
    case START_SELECT:
      return {
        ...state,
        focusing: FOCUSING.DESKTOP,
        icons: state.icons.map(icon => ({
          ...icon,
          isFocus: false,
        })),
        selecting: action.payload,
      };
    case END_SELECT:
      return {
        ...state,
        selecting: null,
      };
    case POWER_OFF:
      return {
        ...state,
        powerState: action.payload,
      };
    case CANCEL_POWER_OFF:
      return {
        ...state,
        powerState: POWER_STATE.START,
      };
    default:
      return state;
  }
};
function WinXP() {
  const [state, dispatch] = useReducer(reducer, initState);
  const [paper, setPaper] = useState(() => {
    try {
      return localStorage.getItem(WALLPAPER_KEY) || null;
    } catch (e) {
      return null; // private mode
    }
  });
  const ref = useRef(null);
  const mouse = useMouse(ref);
  const focusedAppId = getFocusedAppId();
  const onFocusApp = useCallback(id => {
    dispatch({ type: FOCUS_APP, payload: id });
  }, []);
  const onMaximizeWindow = useCallback(
    id => {
      if (focusedAppId === id) {
        dispatch({ type: TOGGLE_MAXIMIZE_APP, payload: id });
      }
    },
    [focusedAppId],
  );
  const onMinimizeWindow = useCallback(
    id => {
      if (focusedAppId === id) {
        dispatch({ type: MINIMIZE_APP, payload: id });
      }
    },
    [focusedAppId],
  );
  const onCloseApp = useCallback(
    id => {
      if (focusedAppId === id) {
        dispatch({ type: DEL_APP, payload: id });
      }
    },
    [focusedAppId],
  );
  function onMouseDownFooterApp(id) {
    if (focusedAppId === id) {
      dispatch({ type: MINIMIZE_APP, payload: id });
    } else {
      dispatch({ type: FOCUS_APP, payload: id });
    }
  }
  function onMouseDownIcon(id) {
    dispatch({ type: FOCUS_ICON, payload: id });
  }
  function spawnError(message, dx = 0, dy = 0) {
    dispatch({
      type: ADD_APP,
      payload: {
        ...appSettings.Error,
        defaultOffset: {
          x: Math.max(8, window.innerWidth / 2 - 190 + dx),
          y: Math.max(8, window.innerHeight / 2 - 60 + dy),
        },
        injectProps: { message },
      },
    });
  }
  function doNotOpen() {
    // The original site had a DO NOT OPEN icon. Ours is defanged: at most three
    // dialogs, each one click to dismiss, never modal, single dialog on phones.
    const lines = [
      'You opened it.\nThe Internet has performed an illegal operation:\nit paid you.',
      'Warning: this window cannot be closed.\n(it can. click OK.)',
      'C:\\\npayday.exe is already running.',
    ];
    if (window.innerWidth < 800) {
      spawnError(lines[0]);
      return;
    }
    lines.forEach((m, i) => setTimeout(() => spawnError(m, i * 46, i * 46), i * 260));
  }
  function onDoubleClickIcon(component, app, href) {
    if (href) return window.open(href, '_blank', 'noopener,noreferrer');
    if (component === DoNotOpen) return doNotOpen();
    const appSetting =
      (app && appSettings[app]) ||
      Object.values(appSettings).find(setting => setting.component === component);
    if (appSetting) dispatch({ type: ADD_APP, payload: appSetting });
  }
  // Open a specific game (Games folder, Start menu, cmd.exe all use this shape).
  function openGameByTitle(title) {
    const g = findGame(title);
    if (!g) return false;
    openGame(g);
    return true;
  }
  function getFocusedAppId() {
    if (state.focusing !== FOCUSING.WINDOW) return -1;
    const focusedApp = [...state.apps]
      .sort((a, b) => b.zIndex - a.zIndex)
      .find(app => !app.minimized);
    return focusedApp ? focusedApp.id : -1;
  }
  function onMouseDownFooter() {
    dispatch({ type: FOCUS_DESKTOP });
  }
  function onClickMenuItem(o) {
    if (o === 'Internet')
      dispatch({ type: ADD_APP, payload: appSettings['Internet Explorer'] });
    else if (o === 'Minesweeper')
      dispatch({ type: ADD_APP, payload: appSettings.Minesweeper });
    else if (o === 'My Computer')
      dispatch({ type: ADD_APP, payload: appSettings['My Computer'] });
    else if (o === 'Notepad')
      dispatch({ type: ADD_APP, payload: appSettings.Notepad });
    else if (o === 'Winamp')
      dispatch({ type: ADD_APP, payload: appSettings.Winamp });
    else if (o === 'Paint')
      dispatch({ type: ADD_APP, payload: appSettings.Paint });
    else if (o === 'MISSION.exe')
      dispatch({ type: ADD_APP, payload: appSettings.Mission });
    else if (o === 'CASINO.exe')
      dispatch({ type: ADD_APP, payload: appSettings.Casino });
    else if (o === 'X (@internetcoin888)') window.open(SOCIAL.x, '_blank', 'noopener,noreferrer');
    else if (o === 'Telegram') window.open(SOCIAL.telegram, '_blank', 'noopener,noreferrer');
    else if (o === 'MSFT.exe')
      dispatch({ type: ADD_APP, payload: appSettings.Msft });
    else if (o === 'PAYDAY.exe')
      dispatch({ type: ADD_APP, payload: appSettings.Payday });
    else if (o === 'VERIFY.exe')
      dispatch({ type: ADD_APP, payload: appSettings.Verify });
    else if (o === 'Network Neighborhood' || o === 'My Network Places')
      dispatch({ type: ADD_APP, payload: appSettings.Network });
    else if (o === 'DIVIDENDS.exe')
      dispatch({ type: ADD_APP, payload: appSettings.Dividends });
    else if (o === 'BUY $INTERNET')
      dispatch({ type: ADD_APP, payload: appSettings.Buy });
    else if (o === 'Pinball' || o === '3D Pinball')
      dispatch({ type: ADD_APP, payload: appSettings.Pinball });
    else if (o === 'My Videos')
      dispatch({ type: ADD_APP, payload: appSettings['My Videos'] });
    else if (o === 'My Pictures')
      dispatch({ type: ADD_APP, payload: appSettings['My Pictures'] });
    else if (o === 'Games' || o === 'More Games...')
      dispatch({ type: ADD_APP, payload: appSettings.Games });
    else if (o === 'Windows Media Player' || o === 'Old Movies')
      dispatch({ type: ADD_APP, payload: appSettings['Windows Media Player'] });
    else if (o === 'Command Prompt')
      dispatch({ type: ADD_APP, payload: appSettings['Command Prompt'] });
    else if (o === 'Time Machine')
      dispatch({ type: ADD_APP, payload: appSettings['Time Machine'] });
    else if (o === 'Internet Explorer')
      dispatch({ type: ADD_APP, payload: appSettings['Internet Explorer'] });
    else if (o === 'Run...')
      dispatch({ type: ADD_APP, payload: appSettings.Run });
    else if (openGameByTitle(o)) {
      /* a game from Start → All Programs → Games opened in its own window */
    }
    else if (o === 'Windows Messenger')
      dispatch(
        appSettings['Windows Messenger']
          ? { type: ADD_APP, payload: appSettings['Windows Messenger'] }
          : {
              type: ADD_APP,
              payload: {
                ...appSettings.Error,
                injectProps: { message: 'Windows Messenger\nMSN is coming online. Not yet, fren.' },
              },
            },
      );
    else if (o === 'Log Off')
      dispatch({ type: POWER_OFF, payload: POWER_STATE.LOG_OFF });
    else if (o === 'Turn Off Computer')
      dispatch({ type: POWER_OFF, payload: POWER_STATE.TURN_OFF });
    else
      dispatch({
        type: ADD_APP,
        payload: {
          ...appSettings.Error,
          injectProps: { message: 'C:\\\nApplication not found' },
        },
      });
  }
  // Practice preview: the casino is the thing being tested, so it opens on top once the boot
  // windows are up — nobody should have to find the icon under the Winamp stack first.
  useEffect(() => {
    if (!CASINO_PRACTICE) return undefined;
    const t = setTimeout(() => dispatch({ type: ADD_APP, payload: appSettings.Casino }), 1500);
    return () => clearTimeout(t);
  }, []);
  // Apps (IE toolbar, tray, Games folder, cmd.exe) open other apps through one window event.
  // detail = 'AppKey' | { app: 'AppKey', props?: {...}, title?: 'window title', key?: 'dedupe key' }
  useEffect(() => {
    function onOpen(e) {
      const d = e.detail;
      const key = typeof d === 'string' ? d : d && d.app;
      const setting = appSettings[key];
      if (!setting) return;
      if (typeof d === 'string') return dispatch({ type: ADD_APP, payload: setting });
      dispatch({
        type: ADD_APP,
        payload: {
          ...setting,
          injectProps: { ...(setting.injectProps || {}), ...(d.props || {}) },
          header: { ...setting.header, ...(d.title ? { title: d.title } : {}) },
          instanceKey: d.key || setting.instanceKey,
        },
      });
    }
    // My Pictures → Set as desktop background (detail null restores the original).
    function onWallpaper(e) {
      const src = e.detail && e.detail.src ? e.detail.src : null;
      setPaper(src);
      try {
        if (src) localStorage.setItem(WALLPAPER_KEY, src);
        else localStorage.removeItem(WALLPAPER_KEY);
      } catch (err) {
        /* private mode */
      }
    }
    // ARG: the key turned on the internal web → BASEMENT.exe lands on the desktop and opens.
    function onUnlock() {
      dispatch({ type: ADD_ICON, payload: basementIconEntry });
      setTimeout(() => dispatch({ type: ADD_APP, payload: appSettings.Basement }), 900);
    }
    window.addEventListener('ic:open', onOpen);
    window.addEventListener('ic:unlock', onUnlock);
    window.addEventListener('ic:wallpaper', onWallpaper);
    return () => {
      window.removeEventListener('ic:open', onOpen);
      window.removeEventListener('ic:unlock', onUnlock);
      window.removeEventListener('ic:wallpaper', onWallpaper);
    };
  }, []);
  function onMouseDownDesktop(e) {
    if (e.target === e.currentTarget)
      dispatch({
        type: START_SELECT,
        payload: { x: mouse.docX, y: mouse.docY },
      });
  }
  function onMouseUpDesktop(e) {
    dispatch({ type: END_SELECT });
  }
  const onIconsSelected = useCallback(
    iconIds => {
      dispatch({ type: SELECT_ICONS, payload: iconIds });
    },
    [dispatch],
  );
  function onClickModalButton(text) {
    dispatch({ type: CANCEL_POWER_OFF });
    dispatch({
      type: ADD_APP,
      payload: appSettings.Error,
    });
  }
  function onModalClose() {
    dispatch({ type: CANCEL_POWER_OFF });
  }
  return (
    <Container
      ref={ref}
      onMouseUp={onMouseUpDesktop}
      onMouseDown={onMouseDownDesktop}
      state={state.powerState}
      paper={paper}
    >
      <Icons
        icons={state.icons}
        onMouseDown={onMouseDownIcon}
        onDoubleClick={onDoubleClickIcon}
        displayFocus={state.focusing === FOCUSING.ICON}
        appSettings={appSettings}
        mouse={mouse}
        selecting={state.selecting}
        setSelectedIcons={onIconsSelected}
      />
      <DashedBox startPos={state.selecting} mouse={mouse} />
      <Windows
        apps={state.apps}
        onMouseDown={onFocusApp}
        onClose={onCloseApp}
        onMinimize={onMinimizeWindow}
        onMaximize={onMaximizeWindow}
        focusedAppId={focusedAppId}
      />
      <Footer
        apps={state.apps}
        onMouseDownApp={onMouseDownFooterApp}
        focusedAppId={focusedAppId}
        onMouseDown={onMouseDownFooter}
        onClickMenuItem={onClickMenuItem}
      />
      <Boot />
      <Screensaver />
      {state.powerState !== POWER_STATE.START && (
        <Modal
          onClose={onModalClose}
          onClickButton={onClickModalButton}
          mode={state.powerState}
        />
      )}
    </Container>
  );
}

const powerOffAnimation = keyframes`
  0% {
    filter: brightness(1) grayscale(0);
  }
  30% {
    filter: brightness(1) grayscale(0);
  }
  100% {
    filter: brightness(0.6) grayscale(1);
  }
`;
const animation = {
  [POWER_STATE.START]: '',
  [POWER_STATE.TURN_OFF]: powerOffAnimation,
  [POWER_STATE.LOG_OFF]: powerOffAnimation,
};

const Container = styled.div`
  @import url('https://fonts.googleapis.com/css?family=Noto+Sans');
  font-family: Tahoma, 'Noto Sans', sans-serif;
  height: 100%;
  overflow: hidden;
  position: relative;
  background: #0a0c04 url(${({ paper }) => paper || wallpaper}) no-repeat center center fixed;
  background-size: cover;
  animation: ${({ state }) => animation[state]} 5s forwards;
  *:not(input):not(textarea) {
    user-select: none;
  }
`;

export default WinXP;
